/**
 * The cockpit's cells file — one row per stock cell the page judged, with its judgement
 * (bata-ui#302 C). Pure: the reads are `./exports.ts`'s.
 *
 * Until 2026-10-05 the file carried the stock position only (state of the position, on hand,
 * committed, demand, cover), so a planner holding it could not tell which cells the page called a
 * stock-out, which it could not evaluate and why, or which no line covered. Each row now carries
 * the page's own verdict for the cell — the same `./size-state.ts` judgement the level 2 table
 * draws, and at level 3 the platform's sizes read, as the grid draws it — plus what is beside an
 * uncovered cell (owner decisions F1/F2): the open alert, stock on the way, a deferred move. None
 * of those is coverage.
 *
 * A verdict the reads could not give (a read failed) is `not-reported` in every verdict column,
 * never a state chosen here; a cell not in assortment has no state (`not-ranged`).
 */

import type { StockPositionSummary } from '@/api/generated';
import type { ExportCell, ExportColumn, ExportTranslate } from '@/export';
import { HEALTH_STATES, type HealthState } from '@/api/pending/network-health';

import { causeWords } from './uncovered-cause';
import { REASON_KEYS, arrivingUnits, storeArticleKey, type HealthReason, type SizeVerdict, type StoreArticleVerdict } from './size-state';

/** The words of a health state — a literal table, so the catalogue check sees every key. */
const STATE_KEYS: Readonly<Record<HealthState, string>> = {
  ok: 'inseason.networkHealth.state.ok',
  low: 'inseason.networkHealth.state.low',
  out: 'inseason.networkHealth.state.out',
  broken: 'inseason.networkHealth.state.broken',
  na: 'inseason.networkHealth.state.na',
};

const YES_NO: Readonly<Record<string, string>> = { yes: 'inseason.networkHealth.export.yes', no: 'inseason.networkHealth.export.no' };

/** A code's words in the workbook; the CSV keeps the code. */
function words(keys: Readonly<Record<string, string>>) {
  return (code: string, translate: ExportTranslate): string => {
    const key = keys[code];
    if (key === undefined) return code;
    const text = translate(key);
    return text === '' || text === key ? code : text;
  };
}

/** The platform's `cellsNaByReason` code for each reason — the CSV's code, the same as the groups file's. */
export const REASON_CODES: Readonly<Record<HealthReason, string>> = {
  notReported: 'position_not_reported',
  notCounted: 'on_hand_not_counted',
  partitionStale: 'partition_stale',
  noDemand: 'no_demand',
  demandNotForecast: 'demand_not_forecast',
  demandBelowFloor: 'demand_below_floor',
  coverRuleAbsent: 'cover_rule_not_applicable',
  notObserved: 'not_observed_for_run',
  other: 'other',
  notServed: 'not_in_partition',
};

const REASON_WORDS: Readonly<Record<string, string>> = Object.fromEntries(
  (Object.keys(REASON_CODES) as HealthReason[]).map((reason) => [REASON_CODES[reason], REASON_KEYS[reason]]),
);

/** The judgement columns, after the position's own. */
export const VERDICT_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'health_state', headerKey: 'inseason.networkHealth.export.healthState', kind: 'label', display: words(STATE_KEYS),
    descriptionKey: 'inseason.networkHealth.columnGuide.healthState',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.reason.notServed', 'not-reported': 'inseason.networkHealth.export.absent.notJudged' } },
  },
  {
    key: 'na_reason', headerKey: 'inseason.networkHealth.export.naReason', kind: 'label', display: words(REASON_WORDS),
    descriptionKey: 'inseason.networkHealth.columnGuide.naReason',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.evaluable', 'not-reported': 'inseason.networkHealth.export.absent.notJudged' } },
  },
  {
    key: 'uncovered', headerKey: 'inseason.networkHealth.export.uncoveredFlag', kind: 'label', display: words(YES_NO),
    descriptionKey: 'inseason.networkHealth.columnGuide.uncovered',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.notAProblem', 'not-reported': 'inseason.networkHealth.export.absent.notJudged' } },
  },
  { key: 'proposal_units', headerKey: 'inseason.networkHealth.export.proposalUnits', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.networkHealth.columnGuide.proposalUnits' },
  { key: 'transfer_in_units', headerKey: 'inseason.networkHealth.export.transferInUnits', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.networkHealth.columnGuide.transferInUnits' },
  { key: 'transfer_out_units', headerKey: 'inseason.networkHealth.export.transferOutUnits', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.networkHealth.columnGuide.transferOutUnits' },
  {
    key: 'arriving_units', headerKey: 'inseason.networkHealth.export.arrivingUnits', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.networkHealth.columnGuide.arrivingUnits',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.notArriving' } },
  },
  {
    key: 'open_alert_rules', headerKey: 'inseason.networkHealth.export.openAlert', kind: 'text', descriptionKey: 'inseason.networkHealth.columnGuide.openAlert',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.noAlert', 'not-reported': 'inseason.networkHealth.export.absent.alertsNotRead' } },
  },
  {
    key: 'price_step', headerKey: 'inseason.networkHealth.export.priceStep', kind: 'label', display: words(YES_NO),
    descriptionKey: 'inseason.networkHealth.columnGuide.priceStep',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.priceStepNotRead' } },
  },
  {
    key: 'move_cap_deferred', headerKey: 'inseason.networkHealth.export.moveCapDeferred', kind: 'label', display: words(YES_NO),
    descriptionKey: 'inseason.networkHealth.columnGuide.moveCapDeferred',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.absent.deferralsNotRead' } },
  },
  {
    key: 'uncovered_reason', headerKey: 'inseason.networkHealth.export.cause.column', kind: 'label',
    display: (code, translate) => causeWords(code, translate),
    descriptionKey: 'inseason.networkHealth.columnGuide.cause.guide',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.networkHealth.export.absent.noCause', 'not-reported': 'inseason.networkHealth.export.cause.absent' } },
  },
  {
    key: 'target_units', headerKey: 'inseason.networkHealth.export.cause.target', kind: 'integer', unit: 'pairs',
    descriptionKey: 'inseason.networkHealth.columnGuide.cause.targetGuide',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.cause.absent' } },
  },
  {
    key: 'need_units', headerKey: 'inseason.networkHealth.export.cause.need', kind: 'integer', unit: 'pairs',
    descriptionKey: 'inseason.networkHealth.columnGuide.cause.needGuide',
    absent: { reasonKeys: { 'not-reported': 'inseason.networkHealth.export.cause.absent' } },
  },
];


const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };
const NOT_RANGED: ExportCell = { kind: 'missing', state: 'not-ranged' };
const label = (code: string): ExportCell => ({ kind: 'label', value: code, code });

/** What the export read beside the positions, each `null` when its read failed. */
export interface CellJudgement {
  /** One verdict per store × article of the positions; `null` when the lines or rules were not read. */
  readonly verdicts: ReadonlyMap<string, StoreArticleVerdict> | null;
  /** Read: whether the alerts were read (an alert list is per store × article, on the verdict). */
  readonly alertsRead: boolean;
  /** The store × articles the move cap deferred a move for; `null` when the deferrals were not read. */
  readonly deferred: ReadonlySet<string> | null;
  /**
   * Whether the tick's price run stepped the article, per store × article — the platform's
   * `hasPriceStep` from the sizes read (level 3 only); absent: not read, or the tick has no price run.
   */
  readonly priceStep?: ReadonlyMap<string, boolean | null>;
}

/** One position's judgement cells, in `VERDICT_COLUMNS` order. Pure. */
export function verdictCells(row: StockPositionSummary, judgement: CellJudgement): readonly ExportCell[] {
  const key = storeArticleKey(row.store_key, row.article_key);
  const deferredCell: ExportCell = judgement.deferred === null ? NOT_REPORTED : label(judgement.deferred.has(key) ? 'yes' : 'no');
  const step = judgement.priceStep?.get(key);
  const priceCell: ExportCell = step === undefined || step === null ? NOT_REPORTED : label(step ? 'yes' : 'no');
  const verdict = judgement.verdicts?.get(key);
  const size: SizeVerdict | undefined = verdict?.sizes.find((candidate) => candidate.name.key === row.size_key);
  if (judgement.verdicts === null || verdict === undefined || size === undefined) {
    return [NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED, priceCell, deferredCell, NOT_REPORTED, NOT_REPORTED, NOT_REPORTED];
  }
  const state = size.state;
  const known = state !== null && (HEALTH_STATES as readonly string[]).includes(state);
  const problem = state === 'out' || state === 'broken' || state === 'low';
  const arriving = arrivingUnits(size);
  const rules = verdict.openAlertRules;
  const cause = size.cause;
  return [
    known ? label(state) : NOT_RANGED,
    size.reason === null || size.reason === 'notServed' ? NOT_RANGED : label(REASON_CODES[size.reason]),
    !problem || size.uncovered === null ? NOT_RANGED : label(size.uncovered ? 'yes' : 'no'),
    { kind: 'integer', raw: size.interventions.proposalUnits },
    { kind: 'integer', raw: size.interventions.transfersIn.reduce((sum, leg) => sum + leg.units, 0) },
    { kind: 'integer', raw: size.interventions.transfersOut.reduce((sum, leg) => sum + leg.units, 0) },
    arriving === null ? NOT_RANGED : { kind: 'integer', raw: arriving },
    !judgement.alertsRead ? NOT_REPORTED : rules.length === 0 ? NOT_RANGED : { kind: 'text', value: rules.join(', ') },
    priceCell,
    deferredCell,
    cause?.reason === undefined || cause.reason === null ? NOT_REPORTED : label(cause.reason),
    cause?.targetUnits === undefined || cause.targetUnits === null ? NOT_REPORTED : { kind: 'integer', raw: cause.targetUnits },
    cause?.needUnits === undefined || cause.needUnits === null ? NOT_REPORTED : { kind: 'integer', raw: cause.needUnits },
  ];
}
